import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Control, Resolver } from 'react-hook-form';
import {
  dealerAddFundsSchema,
  dealerRemoveFundsSchema,
  dealerShareSchema,
  dealerTransferSchema,
} from '@app/shared';
import type {
  CommissionBalanceDto,
  DealerTxnDto,
  DealerWalletDto,
  DealerWalletLogDto,
  DealerWalletRowDto,
  Paged,
} from '@app/shared';
import { LoginIdField } from '../../components/LoginIdSuggest';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { FormDialog } from '../../components/dialogs';
import { MoneyInput, SelectInput, TextInput, applyServerErrors } from '../../components/form';
import { api } from '../../lib/api';
import { formatCents, formatDateTime } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { withRequiredMessages } from '../inventory/formUtils';

const WALLET_KEY = ['dealer-wallet'] as const;

/** Create your wallet, Share Wallet, Other User ID, Activity Log and Remove Funds by wallet. */
const SHARE_FEATURE = 'dealer-wallet-share';

type TabKey = 'wallet' | 'add' | 'transfer' | 'history' | 'activity' | 'remove';

interface MoneyForm {
  amountCents: number | undefined;
  receiptNumber: string;
  toUserId: string;
  otherUserId: string;
}
const EMPTY: MoneyForm = {
  amountCents: undefined,
  receiptNumber: '',
  toUserId: '',
  otherUserId: '',
};

/** Posts to a dealer-wallet action and refreshes the balance, the lists and the logs. */
function useWalletAction(path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<MoneyForm> & { walletUserId?: string }) =>
      api.post<DealerWalletDto>(path, input),
    onSuccess: (next) => {
      queryClient.setQueryData(WALLET_KEY, next);
      return queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'dealer-wallet' && q.queryKey.length > 1,
      });
    },
  });
}

/** "Other User ID": a login ID typed instead of choosing from the list. */
function OtherUserId({ control }: { control: Control<MoneyForm> }) {
  return (
    <Controller
      control={control}
      name="otherUserId"
      render={({ field, fieldState }) => (
        <LoginIdField
          label="Other User ID"
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

function AddFundsForm() {
  const notify = useNotify();
  const add = useWalletAction('/dealer-wallet/add-funds');
  const { control, handleSubmit, reset, setError } = useForm<MoneyForm>({
    defaultValues: EMPTY,
    resolver: withRequiredMessages<MoneyForm>(
      zodResolver(dealerAddFundsSchema) as unknown as Resolver<MoneyForm>,
      { amountCents: 'Amount is required' },
    ),
  });
  const submit = handleSubmit(async ({ amountCents, receiptNumber }) => {
    try {
      const next = await add.mutateAsync({ amountCents, receiptNumber });
      notify.success(`Funds added. Wallet balance ${formatCents(next.balanceCents)}.`);
      reset(EMPTY);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label="Add Funds"
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2, display: 'grid', gap: 2, maxWidth: 420 }}
    >
      <Typography>Payment method: Cash (PC569)</Typography>
      <MoneyInput control={control} name="amountCents" label="Amount ($)" required />
      <TextInput
        control={control}
        name="receiptNumber"
        label="Receipt Number"
        required
        autoComplete="off"
        slotProps={{ htmlInput: { maxLength: 30 } }}
      />
      <Box>
        <Button type="submit" variant="contained" disabled={add.isPending}>
          Add Funds
        </Button>
      </Box>
    </Paper>
  );
}

function TransferForm({ wallet, ownId }: { wallet: DealerWalletDto; ownId: string }) {
  const notify = useNotify();
  const shareOn = useFeatureReleased()(SHARE_FEATURE);
  const transfer = useWalletAction('/dealer-wallet/transfer');
  const { control, handleSubmit, reset, setError } = useForm<MoneyForm>({
    defaultValues: EMPTY,
    resolver: withRequiredMessages<MoneyForm>(
      zodResolver(dealerTransferSchema) as unknown as Resolver<MoneyForm>,
      { amountCents: 'Amount is required', toUserId: 'Select a User' },
    ),
  });
  const others = wallet.wallets.filter((w) => w.userId !== ownId);
  const submit = handleSubmit(async ({ amountCents, toUserId, otherUserId }) => {
    try {
      const next = await transfer.mutateAsync({
        amountCents,
        toUserId,
        ...(shareOn ? { otherUserId } : {}),
      });
      notify.success(`Funds transferred. Wallet balance ${formatCents(next.balanceCents)}.`);
      reset(EMPTY);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label="Transfer Funds"
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2, display: 'grid', gap: 2, maxWidth: 420 }}
    >
      <SelectInput
        control={control}
        name="toUserId"
        label="Transfer To"
        required
        emptyLabel="-- Select a User --"
        options={others.map((w) => ({ value: w.userId, label: `${w.name} (${w.loginId})` }))}
        helperText={others.length === 0 ? 'There is no user below you to transfer to.' : undefined}
      />
      {shareOn ? <OtherUserId control={control} /> : null}
      <MoneyInput control={control} name="amountCents" label="Amount ($)" required />
      <Box>
        <Button type="submit" variant="contained" disabled={transfer.isPending}>
          Transfer Funds
        </Button>
      </Box>
    </Paper>
  );
}

/** "Share Wallet": Select User* or Other User ID. */
function ShareWalletForm({ wallet, ownId }: { wallet: DealerWalletDto; ownId: string }) {
  const notify = useNotify();
  const share = useWalletAction('/dealer-wallet/share');
  const { control, handleSubmit, reset, setError } = useForm<MoneyForm>({
    defaultValues: EMPTY,
    resolver: zodResolver(dealerShareSchema) as unknown as Resolver<MoneyForm>,
  });
  const others = wallet.wallets.filter((w) => w.userId !== ownId);
  const submit = handleSubmit(async ({ toUserId, otherUserId }) => {
    try {
      await share.mutateAsync({ toUserId, otherUserId });
      notify.success('Wallet shared.');
      reset(EMPTY);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label="Share Wallet"
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2, display: 'grid', gap: 2, maxWidth: 420 }}
    >
      {!wallet.hasWallet ? (
        <Alert severity="info">
          Your wallet has not been created yet. Please create your wallet first to share it with
          others.
        </Alert>
      ) : null}
      <SelectInput
        control={control}
        name="toUserId"
        label="Select User"
        required
        emptyLabel="-- Select a User --"
        options={others.map((w) => ({ value: w.userId, label: `${w.name} (${w.loginId})` }))}
      />
      <OtherUserId control={control} />
      {wallet.sharedWith.length > 0 ? (
        <Typography variant="body2">
          Shared with: {wallet.sharedWith.map((u) => u.loginId).join(', ')}
        </Typography>
      ) : null}
      <Box>
        <Button type="submit" variant="contained" disabled={share.isPending || !wallet.hasWallet}>
          Share Wallet
        </Button>
      </Box>
    </Paper>
  );
}

/** "Confirm Fund Removal": Amount ($)*; from Remove Funds' list, of the wallet chosen there. */
function RemoveFundsDialog({
  open,
  onClose,
  walletUserId,
}: {
  open: boolean;
  onClose: () => void;
  walletUserId?: string;
}) {
  const notify = useNotify();
  const remove = useWalletAction('/dealer-wallet/remove-funds');
  const { control, handleSubmit, reset, setError } = useForm<MoneyForm>({
    defaultValues: EMPTY,
    resolver: withRequiredMessages<MoneyForm>(
      zodResolver(dealerRemoveFundsSchema) as unknown as Resolver<MoneyForm>,
      { amountCents: 'Amount is required' },
    ),
  });
  const submit = handleSubmit(async ({ amountCents }) => {
    try {
      const next = await remove.mutateAsync({
        amountCents,
        ...(walletUserId ? { walletUserId } : {}),
      });
      notify.success(
        walletUserId
          ? 'Funds removed.'
          : `Funds removed. Wallet balance ${formatCents(next.balanceCents)}.`,
      );
      reset(EMPTY);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  return (
    <FormDialog
      open={open}
      title="Confirm Fund Removal"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={remove.isPending}
      submitLabel="Confirm"
      maxWidth="xs"
    >
      <Box sx={{ pt: 1, display: 'grid' }}>
        <MoneyInput control={control} name="amountCents" label="Amount ($)" required />
      </Box>
    </FormDialog>
  );
}

const TXN_COLUMNS = (page: number, pageSize: number): Column<DealerTxnDto>[] => [
  { key: '#', header: '#', width: 50, render: (_t, i) => rowNumber(i, page, pageSize) },
  { key: 'at', header: 'Date', nowrap: true, render: (t) => formatDateTime(t.at) },
  { key: 'wallet', header: 'Wallet', render: (t) => t.wallet?.loginId ?? '' },
  { key: 'type', header: 'Transaction Type', render: (t) => t.type },
  { key: 'amount', header: 'Amount', align: 'right', render: (t) => formatCents(t.amountCents) },
  {
    key: 'balance',
    header: 'Balance',
    align: 'right',
    render: (t) => formatCents(t.balanceAfterCents),
  },
  { key: 'counterparty', header: 'Other User ID', render: (t) => t.counterparty?.loginId ?? '' },
  { key: 'receipt', header: 'Receipt Number', render: (t) => t.receiptNumber },
  { key: 'by', header: 'Done By', render: (t) => t.by?.loginId ?? '' },
];

/** The wallets of "Select Wallet": own, those below, and one shared with the caller. */
function walletOptions(wallet: DealerWalletDto) {
  const shared = wallet.sharedWallet;
  return [
    ...wallet.wallets,
    ...(shared && !wallet.wallets.some((w) => w.userId === shared.userId) ? [shared] : []),
  ];
}

const LOG_COLUMNS = (page: number, pageSize: number): Column<DealerWalletLogDto>[] => [
  { key: '#', header: '#', width: 50, render: (_l, i) => rowNumber(i, page, pageSize) },
  { key: 'at', header: 'Date', nowrap: true, render: (l) => formatDateTime(l.at) },
  { key: 'wallet', header: 'Wallet', render: (l) => l.wallet?.loginId ?? '' },
  // Assumption: the reference's columns were not seen (no wallet existed during the walk).
  { key: 'action', header: 'Activity', render: (l) => l.action },
  { key: 'description', header: 'Description', render: (l) => l.description },
  { key: 'by', header: 'Done By', render: (l) => l.by?.loginId ?? '' },
];

/**
 * "Transaction History" and "Activity Log": Record Limit, Select Wallet, From and To Date. With
 * the switch on, the caller's own wallet must exist first, as on the reference.
 */
function WalletHistory({
  wallet,
  kind,
}: {
  wallet: DealerWalletDto;
  kind: 'transactions' | 'activity';
}) {
  const shareOn = useFeatureReleased()(SHARE_FEATURE);
  const list = useListState({ walletUserId: '', dateFrom: '', dateTo: '' }, 10);
  const missing = shareOn && !wallet.hasWallet && !list.params.walletUserId;
  const txns = useQuery({
    queryKey: ['dealer-wallet', 'transactions', list.params],
    queryFn: () => api.get<Paged<DealerTxnDto>>('/dealer-wallet/transactions', list.params),
    placeholderData: (prev) => prev,
    enabled: kind === 'transactions' && !missing,
  });
  const logs = useQuery({
    queryKey: ['dealer-wallet', 'activity', list.params],
    queryFn: () => api.get<Paged<DealerWalletLogDto>>('/dealer-wallet/activity', list.params),
    placeholderData: (prev) => prev,
    enabled: kind === 'activity' && !missing,
  });
  const paging = {
    page: list.page,
    pageSize: list.pageSize,
    onPageChange: list.setPage,
    onPageSizeChange: list.setPageSize,
    // The reference's "Record Limit".
    pageSizeOptions: [10, 20, 30, 50, 100, 120, 150, 200],
  };
  return (
    <>
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          select
          label="Select Wallet"
          value={list.draft.walletUserId}
          onChange={(e) => list.setField('walletUserId', e.target.value)}
        >
          <MenuItem value="">My wallet</MenuItem>
          {walletOptions(wallet).map((w) => (
            <MenuItem key={w.userId} value={w.userId}>
              {w.name} ({w.loginId})
            </MenuItem>
          ))}
        </TextField>
        {(['dateFrom', 'dateTo'] as const).map((key) => (
          <TextField
            key={key}
            type="date"
            label={key === 'dateFrom' ? 'From Date' : 'To Date'}
            value={list.draft[key]}
            onChange={(e) => list.setField(key, e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        ))}
      </FilterPanel>
      {missing ? (
        <Alert severity="warning">
          <strong>Wallet Not Found:</strong> Please create your wallet first to view{' '}
          {kind === 'transactions' ? 'transactions' : 'activity logs'}.
        </Alert>
      ) : kind === 'transactions' ? (
        <DataTable
          label="Transaction History"
          columns={TXN_COLUMNS(list.page, list.pageSize)}
          rows={txns.data?.data}
          rowKey={(t) => t.id}
          loading={txns.isFetching}
          error={txns.error}
          onRetry={() => void txns.refetch()}
          total={txns.data?.total ?? 0}
          {...paging}
        />
      ) : (
        <DataTable
          label="Activity Log"
          columns={LOG_COLUMNS(list.page, list.pageSize)}
          rows={logs.data?.data}
          rowKey={(l) => l.id}
          loading={logs.isFetching}
          error={logs.error}
          onRetry={() => void logs.refetch()}
          total={logs.data?.total ?? 0}
          {...paging}
        />
      )}
    </>
  );
}

/** "Remove Funds (PC604)": Record Limit and Select Wallet; S.No | Effective Wallet ID | Balance | Action. */
function RemoveFundsList({ wallet }: { wallet: DealerWalletDto }) {
  const list = useListState({ walletUserId: '' }, 10);
  const [removing, setRemoving] = useState<DealerWalletRowDto | null>(null);
  const wallets = useQuery({
    queryKey: ['dealer-wallet', 'wallets', list.params],
    queryFn: () => api.get<Paged<DealerWalletRowDto>>('/dealer-wallet/wallets', list.params),
    placeholderData: (prev) => prev,
  });
  const columns: Column<DealerWalletRowDto>[] = [
    {
      key: '#',
      header: 'S.No',
      width: 60,
      render: (_w, i) => rowNumber(i, list.page, list.pageSize),
    },
    { key: 'walletId', header: 'Effective Wallet ID', render: (w) => w.walletId },
    { key: 'user', header: 'User', render: (w) => `${w.name} (${w.loginId})` },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (w) => formatCents(w.balanceCents),
    },
    {
      key: 'action',
      header: 'Action',
      render: (w) => (
        <Button size="small" color="error" onClick={() => setRemoving(w)}>
          Remove Funds
        </Button>
      ),
    },
  ];
  return (
    <>
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          select
          label="Select Wallet"
          value={list.draft.walletUserId}
          onChange={(e) => list.setField('walletUserId', e.target.value)}
        >
          <MenuItem value="">-- Select Wallet --</MenuItem>
          {wallet.wallets.map((w) => (
            <MenuItem key={w.userId} value={w.userId}>
              {w.name} ({w.loginId})
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>
      <DataTable
        label="Remove Funds"
        columns={columns}
        rows={wallets.data?.data}
        rowKey={(w) => w.userId}
        loading={wallets.isFetching}
        error={wallets.error}
        onRetry={() => void wallets.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={wallets.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 30, 50, 100, 120, 150, 200]}
      />
      <RemoveFundsDialog
        open={removing !== null}
        walletUserId={removing?.userId}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}

/** "Dealer Wallet" view: the balance, or "Create your wallet"; with the switch, Share Wallet. */
function WalletView({ wallet, shareOn }: { wallet: DealerWalletDto; shareOn: boolean }) {
  const { user } = useAuth();
  const notify = useNotify();
  const create = useWalletAction('/dealer-wallet/create');
  const shared = wallet.sharedWallet;
  const balance =
    shareOn && !wallet.hasWallet ? (
      <Box>
        <Button
          variant="contained"
          disabled={create.isPending}
          onClick={() =>
            create.mutate(
              {},
              {
                onSuccess: () => notify.success('Wallet created.'),
                onError: (err) => notify.error(err),
              },
            )
          }
        >
          Create your wallet
        </Button>
      </Box>
    ) : (
      <Paper variant="outlined" sx={{ p: 2, maxWidth: 420 }} aria-label="Wallet balance">
        <Typography color="text.secondary">Wallet Balance</Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 600 }}>
          {formatCents(wallet.balanceCents)}
        </Typography>
        <Typography>
          {user?.name} ({user?.loginId})
        </Typography>
        {shareOn ? (
          <Typography variant="body2">Effective Wallet ID: {wallet.walletId}</Typography>
        ) : null}
      </Paper>
    );
  if (!shareOn) return balance;
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {balance}
      {shared ? (
        // Ours: says which wallet pays this user's orders once one is shared with them.
        <Alert severity="info" sx={{ maxWidth: 640 }}>
          {shared.name} ({shared.loginId}) shared wallet {shared.walletId} with you; your
          dealer-wallet orders are paid from it. Its balance is {formatCents(shared.balanceCents)}.
        </Alert>
      ) : null}
      <ShareWalletForm wallet={wallet} ownId={user?.id ?? ''} />
    </Box>
  );
}

/**
 * The caller's commission (switch `commission-redeem`): earned, redeemed into this wallet, and
 * what is left; "Redeem Commission" moves it here when the redeem rule is Manual.
 */
function CommissionPanel() {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const balance = useQuery({
    queryKey: ['dealer-wallet', 'commission'],
    queryFn: () => api.get<CommissionBalanceDto>('/dealer-wallet/commission'),
  });
  const redeem = useMutation({
    mutationFn: () => api.post<CommissionBalanceDto>('/dealer-wallet/commission/redeem', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dealer-wallet'] }),
  });
  const b = balance.data;
  if (!b) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, maxWidth: 560 }} aria-label="Commission">
      <Typography variant="h3" sx={{ mb: 1 }}>
        Commission
      </Typography>
      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, m: 0 }}>
        <dt>Earned</dt>
        <dd style={{ margin: 0 }}>{formatCents(b.earnedCents)}</dd>
        <dt>Redeemed into the wallet</dt>
        <dd style={{ margin: 0 }}>{formatCents(b.redeemedCents)}</dd>
        <dt>To redeem</dt>
        <dd style={{ margin: 0 }}>
          <strong>{formatCents(b.balanceCents)}</strong>
        </dd>
      </Box>
      {b.redeemType === 'Manual' ? (
        <Button
          variant="contained"
          sx={{ mt: 1.5 }}
          disabled={b.balanceCents <= 0 || redeem.isPending}
          onClick={() =>
            void redeem
              .mutateAsync()
              .then(() => notify.success('Commission moved to your wallet.'))
              .catch((err: unknown) => notify.error(err))
          }
        >
          Redeem Commission
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Commission moves to your wallet by itself once it reaches{' '}
          {formatCents(b.thresholdCents ?? 0)}.
        </Typography>
      )}
    </Paper>
  );
}

/**
 * Dealer Wallet (PC544): the signed-in agent's wallet. Funds are added in cash, transferred
 * to users below, and removed (PC604); every movement is in the Transaction History. With the
 * switch `dealer-wallet-share`: "Create your wallet", Share Wallet, Other User ID, the Activity
 * Log and Remove Funds as a list of wallets. Card payments need a gateway and are not built.
 */
export function DealerWalletPage() {
  const { user, can } = useAuth();
  const featureOn = useFeatureReleased();
  const shareOn = featureOn(SHARE_FEATURE);
  const redeemOn = featureOn('commission-redeem');
  const [tab, setTab] = useState<TabKey>('wallet');
  const [removing, setRemoving] = useState(false);
  const wallet = useQuery({
    queryKey: WALLET_KEY,
    queryFn: () => api.get<DealerWalletDto>('/dealer-wallet'),
  });

  const tabs: Array<[TabKey, string]> = [
    ['wallet', 'Dealer Wallet'],
    ...(can('PC569') ? ([['add', 'Add Funds']] as Array<[TabKey, string]>) : []),
    ['transfer', 'Transfer Funds'],
    ['history', 'Transaction History'],
    ...(shareOn ? ([['activity', 'Activity Log']] as Array<[TabKey, string]>) : []),
    ...(shareOn && can('PC604')
      ? ([['remove', 'Remove Funds (PC604)']] as Array<[TabKey, string]>)
      : []),
  ];

  return (
    <Box>
      <PageHeader
        title="Dealer Wallet"
        code="PC544"
        actions={
          can('PC604') && !shareOn ? (
            <Button variant="outlined" color="error" onClick={() => setRemoving(true)}>
              Remove Funds (PC604)
            </Button>
          ) : null
        }
      />
      <Tabs
        value={tab}
        onChange={(_e, value: TabKey) => setTab(value)}
        aria-label="Dealer Wallet views"
        sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map(([key, label]) => (
          <Tab key={key} value={key} label={label} />
        ))}
      </Tabs>
      {wallet.error ? (
        <ErrorState error={wallet.error} onRetry={() => void wallet.refetch()} />
      ) : !wallet.data ? (
        <LoadingState />
      ) : tab === 'wallet' ? (
        <>
          <WalletView wallet={wallet.data} shareOn={shareOn} />
          {redeemOn ? <CommissionPanel /> : null}
        </>
      ) : tab === 'add' ? (
        <AddFundsForm />
      ) : tab === 'transfer' ? (
        <TransferForm wallet={wallet.data} ownId={user?.id ?? ''} />
      ) : tab === 'activity' ? (
        <WalletHistory key="activity" wallet={wallet.data} kind="activity" />
      ) : tab === 'remove' ? (
        <RemoveFundsList wallet={wallet.data} />
      ) : (
        <WalletHistory key="transactions" wallet={wallet.data} kind="transactions" />
      )}
      <RemoveFundsDialog open={removing} onClose={() => setRemoving(false)} />
    </Box>
  );
}
